import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../../../hooks/useAuth';

// material-ui
import { useTheme, alpha } from '@mui/material/styles';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';

// project imports
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';
import Avatar from '../../../../../components/@extended/Avatar';
import MainCard from '../../../../../components/MainCard';
import Transitions from '../../../../../components/@extended/Transitions';
import IconButton from '../../../../../components/@extended/IconButton';
import ButtonBase from '../../../../../components/@extended/ButtonBase';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
// removed image avatar; using initials instead

import { Theme } from '@mui/material/styles';

// tab panel wrapper
function TabPanel({ children, value, index, ...other }: { children: React.ReactNode; value: number; index: number; [key: string]: any }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`
  };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const displayName = user?.name || user?.email || 'Usuario';
  const tooltipTitle = (
    <>
      <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>{displayName}</Typography>
      {user?.email && (
        <Typography variant="caption" sx={{ lineHeight: 1.2 }}>{user.email}</Typography>
      )}
    </>
  );

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && (anchorRef.current as any).contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate('/');
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        color="secondary"
        variant="light"
        sx={(theme: Theme) => ({
          p: 0.2,
          pl: 1.5,
          pr: 1.5,
          color: 'text.primary',
          bgcolor: open ? alpha((theme as any).palette.primary.light, 0.15) : 'transparent',
          borderRadius: 1.5,
          transition: 'background-color 150ms ease, color 150ms ease',
          '&:hover': {
            bgcolor: alpha((theme as any).palette.primary.light, 0.15),
            ...(theme.applyStyles && theme.applyStyles('dark', { bgcolor: alpha((theme as any).palette.primary.dark, 0.2) }))
          },
          ...(theme.applyStyles && theme.applyStyles('dark', { bgcolor: open ? alpha((theme as any).palette.primary.dark, 0.2) : 'transparent' }))
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
          <Tooltip title={tooltipTitle} placement="bottom-start">
            <span>
              <Avatar color="primary" outline={false} size="md">
                {getInitials(displayName)}
              </Avatar>
            </span>
          </Tooltip>
          <Box sx={{ ml: 1.6, display: 'flex', flexDirection: 'column', lineHeight: 1, alignItems: 'flex-start', textAlign: 'left', gap: 0 }}>
            <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>{displayName}</Typography>
            {user?.email && (<Typography variant="caption" color="text.secondary">{user.email}</Typography>
            )}
          </Box>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme: any) => ({ boxShadow: (theme as any).customShadows.z1, width: 350, minWidth: 350, maxWidth: { xs: 250, md: 350 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                          <Tooltip title={tooltipTitle} placement="right-start">
                            <span>
                              <Avatar color="primary" outline={false} size="md">
                                {getInitials(displayName)}
                              </Avatar>
                            </span>
                          </Tooltip>
                          <Stack>
                            <Typography variant="h6">{displayName}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user?.email || ''}
                            </Typography>
                            <Typography  variant="body2" color="text.secondary">{user?.companyName.toUpperCase() || ''}</Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid>
                        <Tooltip title={t('auth.actions.logout')}>
                          <IconButton
                            size="large"
                            sx={(theme: any) => ({
                              color: 'text.primary',
                              transition: 'color 150ms ease, background-color 150ms ease',
                              '&:hover': {
                                color: (theme as any).palette.primary.main,
                                bgcolor: `${(theme as any).palette.primary.light}22`
                              }
                            })}
                            onClick={handleLogout}
                          >
                            <LogoutOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  {/*<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs variant="fullWidth" value={value} onChange={handleChange} aria-label="profile tabs" textColor="primary" indicatorColor="primary">
                      <Tab
                        sx={(theme) => ({
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize',
                          gap: 1.25,
                          color: 'text.secondary',
                          '& .MuiTab-icon': { marginBottom: 0, color: 'text.secondary' },
                          '&.Mui-selected': {
                            color: 'primary.main',
                            '& .MuiTab-icon': { color: 'primary.main' }
                          },
                          '&:hover': {
                            bgcolor: alpha((theme as any).palette.primary.light, 0.12)
                          }
                        })}
                        icon={<UserOutlined />}
                        label="Profile"
                        {...a11yProps(0)}
                      />
                      <Tab
                        sx={(theme) => ({
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize',
                          gap: 1.25,
                          color: 'text.secondary',
                          '& .MuiTab-icon': { marginBottom: 0, color: 'text.secondary' },
                          '&.Mui-selected': {
                            color: 'primary.main',
                            '& .MuiTab-icon': { color: 'primary.main' }
                          },
                          '&:hover': {
                            bgcolor: alpha((theme as any).palette.primary.light, 0.12)
                          }
                        })}
                        icon={<SettingOutlined />}
                        label="Setting"
                        {...a11yProps(1)}
                      />
                    </Tabs>
                  </Box>
                  <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab handleLogout={handleLogout} />
                  </TabPanel>
                  <TabPanel value={value} index={1} dir={theme.direction}>
                    <SettingTab />
                  </TabPanel>
                  */}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

function getInitials(name: string): string
 {
  if (!name) return '';
  const cleaned = name.trim();
  // Si es un email, usar parte antes de @ como nombre base
  const base = cleaned.includes('@') ? cleaned.split('@')[0] : cleaned;
  const tokens = base
    .split(/\s+/)
    .filter(Boolean);

  // Ignorar partículas comunes en nombres en español
  const particles = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'da', 'do', 'das', 'dos']);
  const filtered = tokens.filter((t) => !particles.has(t.toLowerCase()));

  const firstName = filtered[0] || tokens[0] || '';
  const firstSurname = filtered.length > 1 ? filtered[1] : '';

  // Fallback si solo hay una palabra
  if (!firstSurname) {
    const chars = firstName.replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/g, '');
    const a = chars[0] || '';
    const b = chars[1] || '';
    return (a + b).toUpperCase();
  }

  return ((firstName[0] || '') + (firstSurname[0] || '')).toUpperCase();
}

// Removed legacy propTypes


